import getTimepointStats from './getTimepointStats';
import getTargetResponses from './getTargetResponses';
import getNonTargetResponses from './getNonTargetResponses';
import getNewLesionResponses from './getNewLesionResponses';
import { getAssessedTimepoints } from './getTimepoints';
import {
  getImmuneTargetResponse,
  getImmuneNonTargetResponse,
  getImmuneNewLesionTargetResponse,
  getImmuneNewLesionNonTargetResponse,
  getImmuneOverallResponse,
} from './getImmuneResponse';
import { TRACKING_TYPES } from './guideline';

export default function getAIAssessments(
  timepoints,
  rois,
  criteria,
  assessments
) {
  if (timepoints.length === 0) return [];
  if (rois.length === 0) return [];
  if (!criteria?.guidelineId) return [];
  switch (criteria.guidelineId) {
    case 'iRECIST': {
      const ai_assessments = getImmuneAssessments(
        timepoints,
        rois,
        criteria,
        assessments
      );
      return ai_assessments;
    }
    default: {
      /** target lesion responses */
      const targetROIContours = rois.filter(
        roi => roi.trackingType === TRACKING_TYPES.TARGET
      );
      const targetStats = getTimepointStats(
        timepoints,
        targetROIContours,
        [],
        criteria
      );
      const targetResponses = getTargetResponses(targetStats, criteria);

      /** non-target lesion responses */
      const nonTargetROIContours = rois.filter(
        roi => roi.trackingType === TRACKING_TYPES.NONTARGET
      );
      const nonTargetStats = getTimepointStats(
        timepoints,
        nonTargetROIContours,
        [],
        criteria
      );
      const nonTargetResponses = getNonTargetResponses(
        nonTargetStats,
        criteria
      );

      /** new lesion responses */
      const newLesionROIContours = rois.filter(
        roi => roi.trackingType === TRACKING_TYPES.NEW
      );
      const newLesionResponses = getNewLesionResponses(
        timepoints,
        newLesionROIContours,
        criteria
      );

      /** overall responses */
      const assessedTimepoints = getAssessedTimepoints(
        timepoints,
        targetResponses,
        nonTargetResponses,
        newLesionResponses,
        assessments
      );
      const ai_assessments = assessedTimepoints.map((t, index) => ({
        ...t,
        targetStat: targetStats[index],
      }));

      return ai_assessments;
    }
  }
}

export function getImmuneAssessments(timepoints, rois, criteria, assessments) {
  const ai_assessments = [];
  for (let i = 1; i <= timepoints.length; i++) {
    const _timepoints = timepoints.slice(0, i).map(t => {
      const previous_ai_assessment = ai_assessments.find(a => a.id === t.id);
      if (previous_ai_assessment) {
        return {
          ...t,
          target_response: previous_ai_assessment.targetResponse.result,
          non_target_response: previous_ai_assessment.nonTargetResponse.result,
          new_lesion_target_response:
            previous_ai_assessment.newLesionTargetResponse.result,
          new_lesion_non_target_response:
            previous_ai_assessment.newLesionNonTargetResponse.result,
          clinical_status: previous_ai_assessment.clinicalStatus.result,
          overall_response: previous_ai_assessment.overallResponse.result,
        };
      }
      return t;
    });

    const assessment = getImmuneTimepointAssessment(
      _timepoints,
      rois,
      criteria,
      assessments
    );
    ai_assessments.push(assessment);
  }
  return ai_assessments;
}

function getImmuneTimepointAssessment(
  _timepoints,
  rois,
  criteria,
  assessments
) {
  /** target lesion responses */
  const targetROIContours = rois.filter(
    roi => roi.trackingType === TRACKING_TYPES.TARGET
  );
  const targetStats = getTimepointStats(
    _timepoints,
    targetROIContours,
    [],
    criteria
  );
  const targetResponse = getImmuneTargetResponse(targetStats, criteria);

  /** non-target lesion response */
  const nonTargetROIContours = rois.filter(
    roi => roi.trackingType === TRACKING_TYPES.NONTARGET
  );
  const nonTargetStats = getTimepointStats(
    _timepoints,
    nonTargetROIContours,
    [],
    criteria
  );
  const nonTargetResponse = getImmuneNonTargetResponse(
    nonTargetStats,
    criteria
  );

  /** new lesion target response */
  const newLesionTargetROIContours = rois.filter(
    roi => roi.trackingType === TRACKING_TYPES.NEW_TARGET
  );
  const newLesionTargetStats = getTimepointStats(
    _timepoints,
    newLesionTargetROIContours,
    [],
    criteria
  );
  const newLesionNumbers = newLesionTargetROIContours.map(roi => roi.ROINumber);
  const newLesionStatsWithLesions = newLesionTargetStats.map((t, index) => ({
    ...t,
    Lesions: _timepoints[index].Lesions.filter(({ ROINumber }) =>
      newLesionNumbers.includes(ROINumber)
    ),
  }));
  const newLesionTargetResponse = getImmuneNewLesionTargetResponse(
    newLesionStatsWithLesions,
    criteria
  );

  /** new lesion non-target response */
  const newLesionNonTargetROIContours = rois.filter(
    roi => roi.trackingType === TRACKING_TYPES.NEW_NONTARGET
  );
  const newLesionNonTargetStats = getTimepointStats(
    _timepoints,
    newLesionNonTargetROIContours,
    [],
    criteria
  );
  const newLesionNonTargetResponse = getImmuneNewLesionNonTargetResponse(
    newLesionNonTargetStats,
    criteria
  );

  /** override AI response assessments with user assessments */
  const current_timepoint = _timepoints[_timepoints.length - 1];
  const { SeriesInstanceUID } = current_timepoint;
  const assessment =
    assessments.find(a => a.SeriesInstanceUID === SeriesInstanceUID) || {};
  if (assessment.targetResponse) {
    targetResponse.result = assessment.targetResponse;
    targetResponse.isOverridden = true;
  }
  if (assessment.nonTargetResponse) {
    nonTargetResponse.result = assessment.nonTargetResponse;
    nonTargetResponse.isOverridden = true;
  }
  if (assessment.newLesionTargetResponse) {
    newLesionTargetResponse.result = assessment.newLesionTargetResponse;
    newLesionTargetResponse.isOverridden = true;
  }
  if (assessment.newLesionNonTargetResponse) {
    newLesionNonTargetResponse.result = assessment.newLesionNonTargetResponse;
    newLesionNonTargetResponse.isOverridden = true;
  }
  if (assessment.steroidsUse) {
    steroidsUse.result = assessment.steroidsUse;
    steroidsUse.isOverridden = true;
  }
  if (assessment.clinicalStatus) {
    clinicalStatus.result = assessment.clinicalStatus;
    clinicalStatus.isOverridden = true;
  }

  /** overall response */
  const steroidsUse = { result: 'N/A', message: '' };
  const clinicalStatus = { result: 'N/A', message: '' };
  const overallResponse = getImmuneOverallResponse(
    [
      ..._timepoints.slice(0, _timepoints.length - 1),
      {
        target_response: targetResponse.result,
        non_target_response: nonTargetResponse.result,
        new_lesion_target_response: newLesionTargetResponse.result,
        new_lesion_non_target_response: newLesionNonTargetResponse.result,
        steroids_use: steroidsUse.result,
        clinical_status: clinicalStatus.result,
      },
    ],
    criteria
  );

  if (assessment.overallResponse) {
    overallResponse.result = assessment.overallResponse;
    overallResponse.isOverridden = true;
  }

  return {
    id: current_timepoint.id,
    SeriesDate: current_timepoint.SeriesDate,
    SeriesInstanceUID: current_timepoint.SeriesInstanceUID,
    SliceThickness: current_timepoint.SliceThickness,
    targetStat: targetStats[_timepoints.length - 1],
    targetResponse,
    nonTargetResponse,
    newLesionTargetResponse,
    newLesionNonTargetResponse,
    steroidsUse,
    clinicalStatus,
    overallResponse,
  };
}
